import { PARTLY_FAILED_LABEL, partlyFailedSentence } from "@shared/runs/partlyFailed";
import { allTablesEmptySentence, tableNoRows } from "@shared/runs/noRows";
import type { DataflowLastRun } from "@shared/types/dataflows";
import type {
  EntityLoadStatus,
  EntityProgressRow,
  EntityProgressStatus,
  RunEntityStatus,
  RunDetail,
  RunProgressNotification,
  RunStatus,
  RunStepRow,
  RunSummaryRow,
  StepName,
  StepStatus,
} from "@shared/types/runs";
import {
  formatCount,
  formatDuration,
  isTerminalRun,
  LIVE_LOAD_ATTEMPT,
  plural,
  stepStatusWord,
  totalsFromRows,
} from "@/components/runs/runFormatting";
import type { ProgressTransport } from "@/lib/live/InstanceRunFeed";
import {
  FALLBACK_ZONE,
  calendarDayInZone,
  canRenderZone,
  relativeAgo,
} from "@/lib/time/displayTime";
import {
  connectionStepPosition,
  connectionStepRows,
  isTerminalStep,
  runningSentence,
  runStepRows,
  stepLabel,
  stepOfLine,
  stepVerb,
  tablesPhrase,
  type StepVocabularyContext,
} from "./stepVocabulary";

/**
 * What an import is doing right now, as one value every surface reads (Session 10, ux-s10.md §2).
 *
 * `deriveRunActivity` is pure: it takes what the screen already has (a `RunDetail` from the
 * unaudited `GET /runs/:id`, or a `DataflowLastRun` seed from the connections list) plus the
 * live frames this tab has folded, and answers the phase, "step n of m", the running sentence
 * and the table counts. The list, the editor and the run page therefore cannot disagree about
 * the same run, and a test can pin every phase without a socket.
 *
 * The frames are a hint, never the record (the socket is at-most-once): they only ever move a
 * value forward, and the next read replaces them.
 */

export type RunActivityPhase =
  | "loading"
  | "unavailable"
  | "never"
  | "queued"
  | "running"
  | "succeeded"
  | "partial"
  | "failed"
  | "cancelled";

/** One row of the vertical stepper (panel and page densities). */
export interface ActivityStep {
  key: string;
  step: StepName;
  dataflowId: string | null;
  dataflowName: string | null;
  label: string;
  status: StepStatus;
  attempt: number;
  durationSeconds: number | null;
  /** Sanitised by the runner (`sanitiseError`); rendered verbatim, never re-expanded. */
  error: string | null;
  /** The status line under the label: "Succeeded · 1m 12s · 40 tables". */
  description: string;
  /** The status word the line starts with ("Cancelled"), which the step's accessible name reads. */
  statusWord: string;
}

export interface RunActivity {
  runId: string | null;
  phase: RunActivityPhase;
  /** 1-based; null while queued, before the first read, or when the surface cannot know. */
  stepIndex: number | null;
  stepTotal: number | null;
  step: StepName | null;
  /** "Load into Snowflake"; null when the step is not known. */
  stepLabel: string | null;
  /** "Loading into Snowflake · 7 of 40 tables"; null unless running with a known step. */
  sentence: string | null;
  tablesDone: number | null;
  tablesTotal: number | null;
  /** The running step's attempt; a retry reads "attempt 2". */
  attempt: number | null;
  /**
   * #1672: while the running load's push sends the tables' files to Snowflake, when it began;
   * the line then reads "Sending files to Snowflake" with the time so far, never "0 of 40
   * tables". Null for every other step, once the push ended, and for a load with no push.
   */
  sendingFilesSince: string | null;
  /**
   * #2389: while the extract runs, the newest sign that it is moving: the step's start or its
   * connection's newest progress message, from the read (`RunStepRow.lastActivityAt`,
   * `DataflowLastRun.lastActivityAt`) or this tab's frames, whichever is later. Null for every
   * other step, and once the extract is not running.
   */
  lastActivityAt: string | null;
  /**
   * #2389: whether the connector itself has reported anything in this extract (a progress
   * message, from the read's `connectorActivityAt` or this tab's frames). Until it has, the job
   * may still be starting, and the line says so instead of "Still reading".
   */
  connectorReported: boolean;
  /** #2389: the running extract's connector display name, null when the surface does not know. */
  sourceName: string | null;
  /** "Waiting to start: 2 imports ahead of this one." when the capacity reason is known. */
  queuedReason: string | null;
  startedAt: string | null;
  finishedAt: string | null;
  durationSeconds: number | null;
  rowsLoaded: number | null;
  /** Tables of a finished import, for "{rows} rows into {n} tables". */
  tables: number | null;
  /**
   * Tables the load accounted for: loaded, or brought no row by the source (#2392, review: a table
   * the load did not create or left as it was is not one that "did not load").
   */
  tablesLoaded: number | null;
  /**
   * #2392: of `tables`, those this import brought no row for (`tableNoRows`); the finished line
   * counts them apart ("into 12 tables, 8 had no rows"). Absent where the tables are not known.
   */
  tablesNoRows?: number;
  /** The failed step's sanitised error (panel and page only). */
  cause: string | null;
  /** Where a failed import stopped. */
  failedStepIndex: number | null;
  failedStepLabel: string | null;
  transport: ProgressTransport;
  /** The stepper rows; empty when only a seed is known (a list row). */
  steps: ActivityStep[];
}

/* ------------------------------------------------------------------------------------- *
 * Live frames
 * ------------------------------------------------------------------------------------- */

interface StepFrame {
  step: StepName;
  dataflowId: string | null;
  status: StepStatus;
  attempt: number | null;
  at: string;
}

/**
 * The frames this tab has seen for ONE run, folded. Only ever moves forward; `runId` makes a
 * stale fold for another run resolve to nothing (the same derivation `useRunProgress` uses).
 */
export interface LiveRunFrames {
  runId: string;
  /** Keyed `dataflowId::step` (`::step` for a run-level row). */
  steps: ReadonlyMap<string, StepFrame>;
  runStatus: RunStatus | null;
  runStatusAt: string | null;
  /** Extract outcome per table, keyed `dataflowId::entity`. `LOAD_*` frames never land here (S1). */
  extract: ReadonlyMap<string, EntityProgressStatus | null>;
  /** Tables whose load reported an outcome in the current load attempt, per connection. */
  load: ReadonlyMap<string, ReadonlySet<string>>;
  /**
   * #1148: each of those tables' outcome, keyed `dataflowId::entity`, so a table the header counts
   * as loaded reads Loaded in its own row too (`withLiveLoadOutcomes`).
   */
  loadOutcomes: ReadonlyMap<string, EntityLoadStatus>;
  /**
   * #2392 (review): of those, the tables the source sent nothing for, by the frame's closed
   * `noRows`, keyed like `loadOutcomes`, so a running import reads them as the record will.
   */
  loadNoRows?: ReadonlyMap<string, "not_created" | "kept">;
  /**
   * #1672: each connection's push in the current load attempt, by its newest `PUSH_*` frame:
   * sending since `at`, or ended. Keyed by connection id.
   */
  push: ReadonlyMap<string, PushFrame>;
  /**
   * #2389: each connection's newest extract progress frame (`TABLE_*`, the periodic
   * `TABLE_PROGRESS` among them, or `EXTRACT_*`), its `at`, keyed by connection id.
   */
  extractActivity: ReadonlyMap<string, string>;
}

/** #1672: a load's push as its frames left it. */
interface PushFrame {
  sending: boolean;
  at: string;
}

export function emptyFrames(runId: string): LiveRunFrames {
  return {
    runId,
    steps: new Map(),
    runStatus: null,
    runStatusAt: null,
    extract: new Map(),
    load: new Map(),
    loadOutcomes: new Map(),
    push: new Map(),
    extractActivity: new Map(),
  };
}

/** #2389: a frame that shows the extract moving: a connector's own `TABLE_*` or `EXTRACT_*`. */
export function isExtractActivityFrame(
  frame: Pick<RunProgressNotification, "event" | "dataflowId">,
): boolean {
  return (
    frame.dataflowId !== null &&
    (frame.event.startsWith("TABLE_") || frame.event.startsWith("EXTRACT_"))
  );
}

/** #2389: the later of two instants, either of which may be missing or unreadable. */
function laterOf(a: string | null | undefined, b: string | null | undefined): string | null {
  const at = a ? Date.parse(a) : Number.NaN;
  const bt = b ? Date.parse(b) : Number.NaN;
  if (!Number.isFinite(at)) return Number.isFinite(bt) ? (b as string) : null;
  if (!Number.isFinite(bt)) return a as string;
  return bt > at ? (b as string) : (a as string);
}

/** #2389: an extract frame moves its connection's last activity forward, never back. */
function foldExtractActivity(next: LiveRunFrames, frame: RunProgressNotification): LiveRunFrames {
  if (!isExtractActivityFrame(frame)) return next;
  const key = frame.dataflowId ?? "";
  const previous = next.extractActivity.get(key);
  const latest = laterOf(previous, frame.at);
  if (latest === null || latest === previous) return next;
  const extractActivity = new Map(next.extractActivity);
  extractActivity.set(key, latest);
  return { ...next, extractActivity };
}

/** #1672: the words a load reads while its push sends the tables' files. */
export const SENDING_FILES_SENTENCE = "Sending files to Snowflake";

/** #1672: the push frames, which open (`PUSH_STARTED`) and end its phase. */
const PUSH_PHASE_FRAMES: ReadonlyMap<string, boolean> = new Map([
  ["PUSH_STARTED", true],
  ["PUSH_FINISHED", false],
  ["PUSH_FAILED", false],
  ["PUSH_STOPPED", false],
]);

/** A push frame moves the connection's push phase, unless an older frame arrives late. */
function foldPushFrame(next: LiveRunFrames, frame: RunProgressNotification): LiveRunFrames {
  const sending = PUSH_PHASE_FRAMES.get(frame.event);
  if (sending === undefined) return next;
  const key = frame.dataflowId ?? "";
  const previous = next.push.get(key);
  if (previous && previous.at > frame.at) return next;
  const push = new Map(next.push);
  push.set(key, { sending, at: frame.at });
  return { ...next, push };
}

/**
 * A table's load outcome by its frame. `LOAD_ENTITY_FINISHED` is a table the load wrote, except
 * that the load with no destination sends it too, at level `warn` with status `skipped`
 * (`finishEntity` in `runner/steps/loadSnowflake.ts`): that one is a skip (`loadOutcomeOf`), so
 * its row never reads Loaded for a load that loaded nothing.
 */
const LOAD_OUTCOME_EVENTS: ReadonlyMap<string, EntityLoadStatus> = new Map([
  ["LOAD_ENTITY_FINISHED", "succeeded"],
  ["LOAD_ENTITY_FAILED", "failed"],
  ["LOAD_ENTITY_SKIPPED", "skipped"],
]);

/** The outcome a load frame reports (`LOAD_OUTCOME_EVENTS`), a warned finish being a skip. */
function loadOutcomeOf(
  frame: Pick<RunProgressNotification, "event" | "level">,
): EntityLoadStatus | undefined {
  const outcome = LOAD_OUTCOME_EVENTS.get(frame.event);
  if (outcome === "succeeded" && frame.level === "warn") return "skipped";
  return outcome;
}

/** The fold with one connection's load count and outcomes started again (a load (re)starts). */
function restartLoad(next: LiveRunFrames, dataflowId: string | null, keep: boolean): LiveRunFrames {
  const id = dataflowId ?? "";
  const load = new Map(next.load);
  if (keep) load.set(id, new Set());
  else load.delete(id);
  const loadOutcomes = new Map(
    [...next.loadOutcomes].filter(([key]) => !key.startsWith(`${id}::`)),
  );
  const loadNoRows = new Map(
    [...(next.loadNoRows ?? [])].filter(([key]) => !key.startsWith(`${id}::`)),
  );
  // #1672: a load step that runs again has not sent its files yet. `LOAD_STARTED` comes before
  // the push starts, so it leaves the push alone.
  if (keep || !next.push.has(id)) return { ...next, load, loadOutcomes, loadNoRows };
  const push = new Map(next.push);
  push.delete(id);
  return { ...next, load, loadOutcomes, loadNoRows, push };
}

/**
 * True for a load frame, the push's own (#1672) among them: counted apart from the extract rows,
 * never merged into them (S1).
 */
export function isLoadFrame(frame: Pick<RunProgressNotification, "event">): boolean {
  return frame.event.startsWith("LOAD_") || frame.event.startsWith("PUSH_");
}

/** True for a frame that moves a step or the run: the only ones that invalidate (SF3). */
export function isStatusFrame(
  frame: Pick<RunProgressNotification, "step" | "runStatus" | "event">,
): boolean {
  return Boolean(frame.step) || Boolean(frame.runStatus) || frame.event.startsWith("STEP_");
}

/** True once the run itself has finished, whatever the outcome. */
export function isTerminalRunStatus(status: RunStatus | null | undefined): boolean {
  return status !== null && status !== undefined && isTerminalRun(status);
}

function stepKey(dataflowId: string | null, step: StepName): string {
  return `${dataflowId ?? ""}::${step}`;
}

function stepRank(status: StepStatus): number {
  if (status === "pending") return 0;
  if (status === "running") return 1;
  return 2;
}

/** A run-status frame moves the run's status, unless the run already ended. */
function foldRunStatus(next: LiveRunFrames, frame: RunProgressNotification): LiveRunFrames {
  if (!frame.runStatus) return next;
  const terminalNow = next.runStatus !== null && isTerminalRun(next.runStatus);
  if (terminalNow) return next;
  return { ...next, runStatus: frame.runStatus, runStatusAt: frame.at };
}

/** A step frame replaces the step's row when it is newer (or the same instant, further on). */
function foldStepFrame(
  next: LiveRunFrames,
  frame: RunProgressNotification & { step: StepName; stepStatus: StepStatus },
): LiveRunFrames {
  const key = stepKey(frame.dataflowId, frame.step);
  const previous = next.steps.get(key);
  const newer =
    !previous ||
    frame.at > previous.at ||
    (frame.at === previous.at && stepRank(frame.stepStatus) >= stepRank(previous.status));
  if (!newer) return next;
  const steps = new Map(next.steps);
  steps.set(key, {
    step: frame.step,
    dataflowId: frame.dataflowId,
    status: frame.stepStatus,
    attempt: frame.attempt,
    at: frame.at,
  });
  let folded: LiveRunFrames = { ...next, steps };
  // A load that (re)starts begins a fresh count of its tables.
  if (frame.step === "load" && frame.stepStatus === "running" && previous?.status !== "running") {
    folded = restartLoad(folded, frame.dataflowId, false);
  }
  return folded;
}

/** A load outcome adds its table to the connection's count for the current load attempt. */
function foldLoadOutcome(
  next: LiveRunFrames,
  dataflowId: string | null,
  entityName: string,
  outcome: EntityLoadStatus,
  noRows?: "not_created" | "kept",
) {
  const key = dataflowId ?? "";
  const seen = next.load.get(key);
  if (seen?.has(entityName)) return next;
  const load = new Map(next.load);
  load.set(key, new Set([...(seen ?? []), entityName]));
  const loadOutcomes = new Map(next.loadOutcomes);
  loadOutcomes.set(`${key}::${entityName}`, outcome);
  if (!noRows) return { ...next, load, loadOutcomes };
  const loadNoRows = new Map(next.loadNoRows ?? []);
  loadNoRows.set(`${key}::${entityName}`, noRows);
  return { ...next, load, loadOutcomes, loadNoRows };
}

/** An extract frame moves a table's extract outcome; a settled outcome never moves again. */
function foldExtractFrame(
  next: LiveRunFrames,
  frame: RunProgressNotification,
  dataflowId: string,
  entityName: string,
): LiveRunFrames {
  const key = `${dataflowId}::${entityName}`;
  const previous = next.extract.get(key);
  const settled = previous === "succeeded" || previous === "failed" || previous === "skipped";
  if (settled || (previous !== undefined && frame.status === null)) return next;
  if (previous === frame.status) return next;
  const extract = new Map(next.extract);
  extract.set(key, frame.status ?? previous ?? "running");
  return { ...next, extract };
}

/** Fold one frame. Pure; returns the same object when the frame changes nothing. */
export function foldFrame(frames: LiveRunFrames, frame: RunProgressNotification): LiveRunFrames {
  if (frame.runId !== frames.runId) return frames;
  const next = foldRunStatus(frames, frame);
  const { step, stepStatus } = frame;
  if (step && stepStatus) return foldStepFrame(next, { ...frame, step, stepStatus });

  if (frame.event === "LOAD_STARTED") return restartLoad(next, frame.dataflowId, true);
  if (PUSH_PHASE_FRAMES.has(frame.event)) return foldPushFrame(next, frame);
  const outcome = loadOutcomeOf(frame);
  if (outcome && frame.entityName) {
    return foldLoadOutcome(next, frame.dataflowId, frame.entityName, outcome, frame.noRows);
  }
  const active = foldExtractActivity(next, frame);
  if (!isLoadFrame(frame) && frame.entityName && frame.dataflowId) {
    return foldExtractFrame(active, frame, frame.dataflowId, frame.entityName);
  }
  return active;
}

/**
 * #1148: the run's tables with the load outcomes this tab has seen live, so the header's count
 * and each table's row read one per-table state. While the run moves, a table the load reported
 * reads that outcome in its row before the step ends and the record attaches its counts; a row
 * the record already gave an outcome keeps it. A finished run reads the record alone. Returns the
 * same array when nothing changes.
 */
export function withLiveLoadOutcomes(
  rows: EntityProgressRow[],
  frames: LiveRunFrames | null | undefined,
  runStatus: RunStatus,
): EntityProgressRow[] {
  if (!frames || frames.loadOutcomes.size === 0 || isTerminalRun(runStatus)) return rows;
  let changed = false;
  const merged = rows.map((row) => {
    if (row.load) return row;
    const key = `${row.dataflowId}::${row.entityName}`;
    const outcome = frames.loadOutcomes.get(key);
    if (!outcome) return row;
    changed = true;
    const noRows = frames.loadNoRows?.get(key);
    return {
      ...row,
      load: {
        status: outcome,
        targetTable: null,
        // #2392 (review): a table the source sent nothing for reads its record's 0 and outcome.
        rowsLoaded: noRows ? 0 : null,
        ...(noRows ? { noRows } : {}),
        expectedRows: null,
        totalRecords: null,
        durationSeconds: null,
        error: null,
        attempt: LIVE_LOAD_ATTEMPT,
      },
    };
  });
  return changed ? merged : rows;
}

/** A run detail whose tables carry the live load outcomes (`withLiveLoadOutcomes`). */
export function withLiveLoads<T extends RunDetail | undefined>(
  detail: T,
  frames: LiveRunFrames | null | undefined,
): T {
  if (!detail || !frames || frames.runId !== detail.runId) return detail;
  const entities = withLiveLoadOutcomes(detail.entities, frames, detail.status);
  return entities === detail.entities ? detail : { ...detail, entities };
}

/* ------------------------------------------------------------------------------------- *
 * Seeds
 * ------------------------------------------------------------------------------------- */

const TERMINAL_STEP_STATUSES: readonly StepStatus[] = [
  "succeeded",
  "failed",
  "skipped",
  "cancelled",
];

/**
 * A runs-list row as a seed (a schedule's History, Session 11 P3): the step counts are
 * every row of the run, which is the run page's m.
 */
export function seedFromRunSummary(row: RunSummaryRow): DataflowLastRun {
  const total = Object.values(row.stepCounts).reduce((sum, value) => sum + value, 0);
  const done = TERMINAL_STEP_STATUSES.reduce((sum, key) => sum + (row.stepCounts[key] ?? 0), 0);
  return {
    runId: row.runId,
    status: row.status,
    startedAt: row.startedAt,
    finishedAt: row.finishedAt,
    durationSeconds: row.durationSeconds,
    rowsExtracted: row.totals?.rowsExtracted ?? null,
    rowsLoaded: row.totals?.rowsLoaded ?? null,
    ...(total > 0 ? { stepsTotal: total, stepsDone: done } : {}),
  };
}

/* ------------------------------------------------------------------------------------- *
 * Derivation
 * ------------------------------------------------------------------------------------- */

export interface DeriveRunActivityInput {
  runId: string | null;
  /** A connection surface's connection; null is the run page (every row counts). */
  dataflowId: string | null;
  detail?: RunDetail;
  seed?: DataflowLastRun | null;
  frames?: LiveRunFrames | null;
  vocabulary?: StepVocabularyContext;
  transport?: ProgressTransport;
  /** True while the first read is in flight. */
  loading?: boolean;
  /** True when the run could not be read (purged, or not this user's to read). */
  unavailable?: boolean;
  /** `RUN_QUEUED_CAPACITY.payload.queuedAhead`, when the caller has read it. */
  queuedAhead?: number | null;
}

function phaseOf(status: RunStatus): RunActivityPhase {
  switch (status) {
    case "queued":
      return "queued";
    case "running":
      return "running";
    case "succeeded":
      return "succeeded";
    case "partially_succeeded":
      return "partial";
    case "failed":
      return "failed";
    case "cancelled":
      return "cancelled";
  }
}

function blank(
  runId: string | null,
  phase: RunActivityPhase,
  transport: ProgressTransport,
): RunActivity {
  return {
    runId,
    phase,
    stepIndex: null,
    stepTotal: null,
    step: null,
    stepLabel: null,
    sentence: null,
    tablesDone: null,
    tablesTotal: null,
    attempt: null,
    sendingFilesSince: null,
    lastActivityAt: null,
    connectorReported: false,
    sourceName: null,
    queuedReason: null,
    startedAt: null,
    finishedAt: null,
    durationSeconds: null,
    rowsLoaded: null,
    tables: null,
    tablesLoaded: null,
    cause: null,
    failedStepIndex: null,
    failedStepLabel: null,
    transport,
    steps: [],
  };
}

/** A detail row with any newer frame for it applied. */
function withFrame(row: RunStepRow, frames: LiveRunFrames | null): RunStepRow {
  const frame = frames?.steps.get(stepKey(row.dataflowId, row.step));
  if (!frame) return row;
  const rowAt = row.finishedAt ?? row.startedAt;
  const frameWins =
    stepRank(frame.status) > stepRank(row.status) || (rowAt !== null && frame.at > rowAt);
  if (!frameWins || frame.status === row.status) return row;
  return { ...row, status: frame.status, attempt: frame.attempt ?? row.attempt };
}

function stepDescription(
  row: RunStepRow,
  tables: number | null,
  terminalRun: boolean,
  sending = false,
): string {
  const parts: string[] = [];
  parts.push(stepStatusWord(row.status, terminalRun));
  if (sending) parts.push(SENDING_FILES_SENTENCE);
  if (row.attempt > 1) parts.push(`attempt ${row.attempt}`);
  if (row.durationSeconds !== null && isTerminalStep(row.status)) {
    parts.push(formatDuration(row.durationSeconds));
  }
  if (tables !== null && tables > 0 && (row.step === "extract" || row.step === "load")) {
    parts.push(`${formatCount(tables)} ${tables === 1 ? "table" : "tables"}`);
  }
  return parts.join(" · ");
}

function entitiesIn(detail: RunDetail, dataflowId: string | null): EntityProgressRow[] {
  return dataflowId === null
    ? detail.entities
    : detail.entities.filter((row) => row.dataflowId === dataflowId);
}

function extractCounts(
  rows: readonly EntityProgressRow[],
  frames: LiveRunFrames | null,
  dataflowId: string | null,
): { done: number; total: number; reported: number } {
  const statuses = new Map<string, RunEntityStatus | null>();
  for (const row of rows) statuses.set(`${row.dataflowId}::${row.entityName}`, row.status);
  if (frames) {
    for (const [key, status] of frames.extract) {
      if (dataflowId !== null && !key.startsWith(`${dataflowId}::`)) continue;
      const previous = statuses.get(key);
      const settled = previous === "succeeded" || previous === "failed" || previous === "skipped";
      // #1127: a frame for a planned (waiting) table means it has started, whatever it says.
      const before = previous === "waiting" ? undefined : previous;
      if (!settled) statuses.set(key, status ?? before ?? "running");
    }
  }
  let done = 0;
  let waiting = 0;
  for (const status of statuses.values()) {
    if (status === "succeeded" || status === "failed" || status === "skipped") done += 1;
    if (status === "waiting") waiting += 1;
  }
  // `total` is what the extract set out to read; `reported` leaves out the planned tables that
  // never reported, which the load is never given.
  return { done, total: statuses.size, reported: statuses.size - waiting };
}

function loadDoneFromFrames(frames: LiveRunFrames | null, dataflowId: string | null): number {
  if (!frames) return 0;
  if (dataflowId !== null) return frames.load.get(dataflowId)?.size ?? 0;
  let sum = 0;
  for (const seen of frames.load.values()) sum += seen.size;
  return sum;
}

/**
 * #1672: when the load's push began sending, or null. The read's answer, moved by this tab's push
 * frames: an end newer than the read's start ends it, a start the read has not seen opens it. A
 * table the load already finished means the push is over, whatever a missed frame left.
 */
function sendingFilesSinceOf(
  recorded: string | null | undefined,
  frames: LiveRunFrames | null,
  dataflowId: string | null,
  tablesDone: number | null,
): string | null {
  const frame = dataflowId === null ? undefined : frames?.push.get(dataflowId);
  let since = recorded ?? null;
  // A load step that ran again after the recorded start is a new attempt, which has not sent yet.
  if (since !== null && loadRestartedAfter(frames, dataflowId, since)) since = null;
  if (frame && !frame.sending && (since === null || frame.at >= since)) since = null;
  else if (frame?.sending && since === null) since = frame.at;
  if (since !== null && tablesDone !== null && tablesDone > 0) return null;
  return since;
}

/** True when this tab saw the connection's load step start running after `since`. */
function loadRestartedAfter(
  frames: LiveRunFrames | null,
  dataflowId: string | null,
  since: string,
): boolean {
  for (const step of frames?.steps.values() ?? []) {
    if (step.step !== "load" || step.status !== "running") continue;
    if (dataflowId !== null && step.dataflowId !== dataflowId) continue;
    if (step.at > since) return true;
  }
  return false;
}

function nullIfZero(value: number): number | null {
  return value > 0 ? value : null;
}

/** The input's frames, read and seed, each only when it belongs to `runId`. */
function partsForRun(
  input: DeriveRunActivityInput,
  runId: string | null,
): {
  frames: LiveRunFrames | null;
  detail: RunDetail | undefined;
  seed: DataflowLastRun | undefined;
} {
  return {
    frames: input.frames && runId && input.frames.runId === runId ? input.frames : null,
    detail: input.detail && input.detail.runId === runId ? input.detail : undefined,
    seed: input.seed && input.seed.runId === runId ? input.seed : undefined,
  };
}

/** True once any step frame has moved past pending. */
function anyStepStarted(frames: LiveRunFrames): boolean {
  for (const frame of frames.steps.values()) {
    if (frame.status !== "pending") return true;
  }
  return false;
}

/**
 * The status: the read's, moved forward by a frame (a terminal frame wins, a running frame
 * lifts a queued read), never backwards.
 */
function forwardStatus(
  detail: RunDetail | undefined,
  seed: DataflowLastRun | undefined,
  frames: LiveRunFrames | null,
): { status: RunStatus | undefined; finishedAt: string | null } {
  let status: RunStatus | undefined = detail?.status ?? seed?.status;
  let finishedAt = detail?.finishedAt ?? seed?.finishedAt ?? null;
  const baseTerminal = status !== undefined && isTerminalRun(status);
  if (frames?.runStatus && !baseTerminal) {
    if (isTerminalRun(frames.runStatus)) {
      status = frames.runStatus;
      finishedAt = finishedAt ?? frames.runStatusAt;
    } else if (status === undefined || status === "queued") {
      status = frames.runStatus;
    }
  }
  if (status === "queued" && frames && anyStepStarted(frames)) status = "running";
  return { status, finishedAt };
}

/** A run id with no status yet: unavailable, loading, or queued. */
function statuslessActivity(
  input: DeriveRunActivityInput,
  runId: string,
  frames: LiveRunFrames | null,
  transport: ProgressTransport,
): RunActivity {
  if (input.unavailable) return blank(runId, "unavailable", transport);
  // The start response's run id before the first read: queued, never "Ready" alone.
  if (input.loading && !frames) return blank(runId, "loading", transport);
  return { ...blank(runId, "queued", transport), queuedReason: queuedReasonOf(input) };
}

/** The pure core: see the module comment. */
export function deriveRunActivity(input: DeriveRunActivityInput): RunActivity {
  const transport = input.transport ?? "disabled";
  const runId = input.runId;
  const { frames, detail, seed } = partsForRun(input, runId);
  const vocabulary = input.vocabulary ?? {};

  if (!runId) return blank(null, input.loading ? "loading" : "never", transport);

  const { status, finishedAt } = forwardStatus(detail, seed, frames);
  if (status === undefined) return statuslessActivity(input, runId, frames, transport);

  const phase = phaseOf(status);
  const terminal = isTerminalRun(status);
  const activity: RunActivity = {
    ...blank(runId, phase, transport),
    startedAt: detail?.startedAt ?? seed?.startedAt ?? null,
    finishedAt: terminal ? finishedAt : null,
    durationSeconds: terminal ? (detail?.durationSeconds ?? seed?.durationSeconds ?? null) : null,
    queuedReason: phase === "queued" ? queuedReasonOf(input) : null,
  };

  if (detail) return fromDetail(activity, detail, input.dataflowId, frames, vocabulary, seed);
  if (seed) return fromSeed(activity, seed, input.dataflowId, frames, vocabulary);
  return activity;
}

function queuedReasonOf(input: DeriveRunActivityInput): string | null {
  // Round 6, T1: a transform waiting for the instance's one dbt slot names the run it waits for.
  const behind = input.detail?.transformQueuedBehind;
  if (behind) return `Waiting for run ${behind}: dbt runs one at a time on an instance.`;
  const ahead = input.queuedAhead;
  if (ahead === null || ahead === undefined || ahead < 1) return null;
  return `Waiting to start: ${ahead} ${ahead === 1 ? "import" : "imports"} ahead of this one.`;
}

/**
 * The running (else the first pending) row of a read, and its step number: that row's own
 * position in the list the Steps card shows, so the header's number and name are one step
 * (#1236). Connections of one run can run side by side; the first running row is the one named.
 * With nothing running or pending, the steps done plus one.
 */
function detailCurrentStep(
  rows: readonly RunStepRow[],
  done: number,
): { index: number; current: RunStepRow | undefined } {
  const total = rows.length;
  let position = rows.findIndex((row) => row.status === "running");
  if (position < 0) position = rows.findIndex((row) => row.status === "pending");
  if (position >= 0) return { current: rows[position], index: position + 1 };
  return { current: undefined, index: Math.min(done + 1, total) };
}

/**
 * A running load's table counts. #1148: the tables done are the connection's rows that carry a
 * load outcome (the record's, or one seen live, `withLiveLoadOutcomes`), the same rows the Tables
 * view reads Loaded, so the header never counts a table its row still reads Loading. A seed (a
 * connection surface with no rows yet) may be further on.
 */
function detailLoadTables(
  rows: readonly EntityProgressRow[],
  seed: DataflowLastRun | undefined,
  extractReported: number,
): { tablesDone: number; tablesTotal: number | null } {
  const rowsDone = rows.filter((row) => row.load).length;
  const seedLoad = seed?.currentStep === "load" ? seed : undefined;
  return {
    tablesDone: Math.max(rowsDone, seedLoad ? (seedLoad.tablesDone ?? 0) : 0),
    tablesTotal: (seedLoad ? seedLoad.tablesTotal : null) ?? nullIfZero(extractReported),
  };
}

/**
 * #1202: one step, one name. An extract names its connection ("Extract from HR") on a run of
 * one connection and of several alike, so no surface adds a second "(HR)" to it
 * (`namesItsConnection`). The connector fallback "the source" is only for a row with no connection.
 */
function rowLabel(row: RunStepRow, vocabulary: StepVocabularyContext): string {
  if (row.step === "extract" && row.dataflowName && !vocabulary.connector) {
    return stepLabel(row.step, { ...vocabulary, connector: row.dataflowName });
  }
  return stepLabel(row.step, vocabulary);
}

/** True for a step whose label already carries its connection's name, so no suffix follows. */
export function namesItsConnection(step: {
  step: StepName;
  label: string;
  dataflowName: string | null;
}): boolean {
  return step.step === "extract" && !!step.dataflowName && step.label.endsWith(step.dataflowName);
}

/** What one step row of a detail is read against. */
interface StepRowContext {
  entities: readonly EntityProgressRow[];
  frames: LiveRunFrames | null;
  terminalRun: boolean;
  vocabulary: StepVocabularyContext;
}

/** One row of the Steps card, from a detail's step row. */
function activityStepOf(row: RunStepRow, ctx: StepRowContext): ActivityStep {
  const { entities, frames, terminalRun, vocabulary } = ctx;
  const own = entities.filter((entity) => entity.dataflowId === row.dataflowId);
  return {
    key: stepKey(row.dataflowId, row.step),
    step: row.step,
    dataflowId: row.dataflowId,
    dataflowName: row.dataflowName,
    label: rowLabel(row, vocabulary),
    status: row.status,
    attempt: row.attempt,
    durationSeconds: row.durationSeconds,
    error: row.error,
    description: stepDescription(
      row,
      stepTablesOf(row, own, frames),
      terminalRun,
      stepSendingOf(row, own, frames, terminalRun),
    ),
    statusWord: stepStatusWord(row.status, terminalRun),
  };
}

/**
 * #1102: a step counts its own connection's tables, never the run's. A row with no connection
 * (transform, finalize) has no table count.
 */
function stepTablesOf(
  row: RunStepRow,
  own: readonly EntityProgressRow[],
  frames: LiveRunFrames | null,
): number | null {
  if (row.step !== "extract" || row.dataflowId === null) return null;
  return nullIfZero(extractCounts(own, frames, row.dataflowId).total);
}

/** Whether a running load's push is sending its files, while the run is not over. */
function stepSendingOf(
  row: RunStepRow,
  own: readonly EntityProgressRow[],
  frames: LiveRunFrames | null,
  terminalRun: boolean,
): boolean {
  if (terminalRun || row.step !== "load" || row.status !== "running") return false;
  const loaded = own.filter((entity) => entity.load).length;
  return sendingFilesSinceOf(row.sendingFilesSince, frames, row.dataflowId, loaded) !== null;
}

/** What the running or next step of a detail is read against. */
interface CurrentStepContext {
  dataflowId: string | null;
  entities: readonly EntityProgressRow[];
  extract: { done: number; total: number; reported: number };
  frames: LiveRunFrames | null;
  seed: DataflowLastRun | undefined;
  vocabulary: StepVocabularyContext;
}

/** The header's step, its tables and its sentence, from the step running now or next. */
function applyCurrentStep(
  activity: RunActivity,
  current: RunStepRow,
  ctx: CurrentStepContext,
): void {
  const { dataflowId, entities, extract, frames, seed, vocabulary } = ctx;
  activity.step = current.step;
  activity.stepLabel = rowLabel(current, vocabulary);
  activity.attempt = current.attempt;
  // #1102: the running step names one connection, so its tables are that connection's. A row
  // with no connection keeps the run's figure.
  const ownId = current.dataflowId ?? dataflowId;
  const ownRows =
    ownId === dataflowId ? entities : entities.filter((entity) => entity.dataflowId === ownId);
  const own = ownId === dataflowId ? extract : extractCounts(ownRows, frames, ownId);
  if (current.step === "extract") {
    activity.tablesDone = own.total > 0 ? own.done : null;
    activity.tablesTotal = nullIfZero(own.total);
    if (current.status === "running") {
      applyExtractLiveness(
        activity,
        current.lastActivityAt,
        current.connectorActivityAt,
        frames,
        ownId,
        vocabulary,
      );
    }
  } else if (current.step === "load") {
    Object.assign(activity, detailLoadTables(ownRows, seed, own.reported));
    if (current.status === "running") {
      // The run read is newer than the connection's seed and names this step's own connection:
      // a running load row without the field has ended its push (or not started it).
      activity.sendingFilesSince = sendingFilesSinceOf(
        current.sendingFilesSince,
        frames,
        ownId,
        activity.tablesDone,
      );
    }
  }
  activity.sentence =
    current.status === "running"
      ? runningOrSending(activity, current.step, vocabulary)
      : pendingSentence(current);
}

/**
 * #2389: a running extract's last activity (the read's, moved forward by this tab's frames) and
 * its connector's name, for the liveness line. A connection the vocabulary does not name has no
 * name, and the line reads "the source".
 */
function applyExtractLiveness(
  activity: RunActivity,
  recorded: string | null | undefined,
  connectorRecorded: string | null | undefined,
  frames: LiveRunFrames | null,
  dataflowId: string | null,
  vocabulary: StepVocabularyContext,
): void {
  const framed = dataflowId === null ? undefined : frames?.extractActivity.get(dataflowId);
  activity.lastActivityAt = laterOf(recorded, framed);
  activity.connectorReported = laterOf(connectorRecorded, framed) !== null;
  activity.sourceName =
    dataflowId === null ? null : (vocabulary.sourceNames?.get(dataflowId) ?? null);
}

function fromDetail(
  base: RunActivity,
  detail: RunDetail,
  dataflowId: string | null,
  frames: LiveRunFrames | null,
  vocabulary: StepVocabularyContext,
  seed: DataflowLastRun | undefined,
): RunActivity {
  const scoped =
    dataflowId === null
      ? runStepRows(detail.steps, detail.dataflowIds)
      : connectionStepRows(detail.steps, dataflowId);
  const rows = scoped.map((row) => withFrame(row, frames));
  const terminalRun = base.phase !== "queued" && base.phase !== "running";
  const entities = withLiveLoadOutcomes(entitiesIn(detail, dataflowId), frames, detail.status);
  const totals = totalsFromRows(entities);
  const extract = extractCounts(entities, frames, dataflowId);
  const noRowsTables = entities.filter(
    (row) => row.status !== "waiting" && tableNoRows(row) !== null,
  ).length;
  const loadedTables = entities.filter(
    (row) =>
      row.load?.status === "succeeded" || (row.status !== "waiting" && tableNoRows(row) !== null),
  ).length;

  const steps: ActivityStep[] = rows.map((row) =>
    activityStepOf(row, { entities, frames, terminalRun, vocabulary }),
  );

  const total = rows.length;
  const done = rows.filter((row) => isTerminalStep(row.status)).length;
  const failedPosition = rows.findIndex((row) => row.status === "failed");
  const activity: RunActivity = {
    ...base,
    stepTotal: total > 0 ? total : null,
    steps,
    rowsLoaded: totals?.rowsLoaded ?? null,
    tables: totals?.tables ?? null,
    tablesLoaded: totals ? loadedTables : null,
    ...(totals && noRowsTables > 0 ? { tablesNoRows: noRowsTables } : {}),
  };

  if (failedPosition >= 0) {
    const failed = rows[failedPosition] as RunStepRow;
    activity.failedStepIndex = failedPosition + 1;
    activity.failedStepLabel = rowLabel(failed, vocabulary);
    activity.cause = failed.error;
  }

  if (base.phase !== "running" || total === 0) return activity;

  const { index, current } = detailCurrentStep(rows, done);
  activity.stepIndex = index;
  if (!current) return activity;

  applyCurrentStep(activity, current, { dataflowId, entities, extract, frames, seed, vocabulary });
  return activity;
}

/** The running sentence, or #1672's while the load's push sends the tables' files. */
function runningOrSending(
  activity: RunActivity,
  step: StepName,
  vocabulary: StepVocabularyContext,
): string {
  if (activity.sendingFilesSince !== null) return SENDING_FILES_SENTENCE;
  return runningSentence(step, vocabulary, {
    done: activity.tablesDone,
    total: activity.tablesTotal,
  });
}

/** #1651: the header's words for a load that waits for a slot of the import limit. */
export const LOAD_WAITING_FOR_SLOT_SENTENCE = "Waiting to load: the import limit is reached.";

/** A pending current step says why it waits, when the server said (#1651); else nothing. */
function pendingSentence(current: RunStepRow): string | null {
  return current.step === "load" && current.status === "pending" && current.waitingForSlot
    ? LOAD_WAITING_FOR_SLOT_SENTENCE
    : null;
}

/** True when `frame` is outside the connection the surface is scoped to. */
function otherConnection(frame: StepFrame, dataflowId: string | null): boolean {
  return dataflowId !== null && frame.dataflowId !== null && frame.dataflowId !== dataflowId;
}

/**
 * A seeded run's steps done and running step, moved forward by the live step frames. A step at
 * or before the steps done is no longer the current one.
 */
function seedProgress(
  seed: DataflowLastRun,
  frames: LiveRunFrames | null,
  dataflowId: string | null,
  total: number,
): { done: number; current: StepName | null } {
  let done = seed.stepsDone ?? 0;
  let current: StepName | null = seed.currentStep ?? null;
  let currentAt = seed.currentStepStartedAt ?? "";
  for (const frame of frames?.steps.values() ?? []) {
    if (otherConnection(frame, dataflowId)) continue;
    const position = connectionStepPosition(frame.step, total);
    if (isTerminalStep(frame.status)) {
      done = Math.max(done, position);
    } else if (frame.status === "running" && frame.at >= currentAt) {
      current = frame.step;
      currentAt = frame.at;
    }
  }
  if (current && connectionStepPosition(current, total) <= done) current = null;
  return { done, current };
}

/** The live extract frames' tables for the connection: how many, and how many settled. */
function liveExtractTables(
  frames: LiveRunFrames | null,
  dataflowId: string | null,
): { liveDone: number; liveTotal: number } {
  let liveDone = 0;
  let liveTotal = 0;
  for (const [key, status] of frames?.extract ?? []) {
    if (dataflowId !== null && !key.startsWith(`${dataflowId}::`)) continue;
    liveTotal += 1;
    if (status === "succeeded" || status === "failed" || status === "skipped") liveDone += 1;
  }
  return { liveDone, liveTotal };
}

/** A seeded run's table counts for its running step; nothing for a step without tables. */
function seedTables(
  current: StepName,
  seed: DataflowLastRun,
  frames: LiveRunFrames | null,
  dataflowId: string | null,
): Pick<RunActivity, "tablesDone" | "tablesTotal"> | null {
  const seedCounts = seed.currentStep === current;
  const seedDone = seedCounts ? (seed.tablesDone ?? 0) : 0;
  if (current === "extract") {
    const { liveDone, liveTotal } = liveExtractTables(frames, dataflowId);
    const totalTables = Math.max(seedCounts ? (seed.tablesTotal ?? 0) : 0, liveTotal);
    return {
      tablesTotal: nullIfZero(totalTables),
      tablesDone: totalTables > 0 ? Math.max(seedDone, liveDone) : null,
    };
  }
  if (current === "load") {
    const liveDone = loadDoneFromFrames(frames, dataflowId);
    return {
      tablesDone: Math.max(seedDone, liveDone),
      tablesTotal: seedCounts ? (seed.tablesTotal ?? null) : null,
    };
  }
  return null;
}

function fromSeed(
  base: RunActivity,
  seed: DataflowLastRun,
  dataflowId: string | null,
  frames: LiveRunFrames | null,
  vocabulary: StepVocabularyContext,
): RunActivity {
  const activity: RunActivity = { ...base, rowsLoaded: seed.rowsLoaded };
  const total = seed.stepsTotal ?? null;
  if (total === null || total < 1) return activity;
  activity.stepTotal = total;
  if (base.phase !== "running") return activity;

  const { done, current } = seedProgress(seed, frames, dataflowId, total);
  activity.stepIndex = current ? connectionStepPosition(current, total) : Math.min(done + 1, total);
  if (!current) return activity;

  activity.step = current;
  activity.stepLabel = stepLabel(current, vocabulary);
  const tables = seedTables(current, seed, frames, dataflowId);
  if (tables) Object.assign(activity, tables);
  if (current === "extract") {
    const inExtract = seed.currentStep === "extract";
    applyExtractLiveness(
      activity,
      inExtract ? seed.lastActivityAt : undefined,
      inExtract ? seed.connectorActivityAt : undefined,
      frames,
      dataflowId,
      vocabulary,
    );
  }
  if (current === "load") {
    const seedSince = seed.currentStep === "load" ? seed.sendingFilesSince : undefined;
    activity.sendingFilesSince = sendingFilesSinceOf(
      seedSince,
      frames,
      dataflowId,
      activity.tablesDone,
    );
  }
  activity.sentence = runningOrSending(activity, current, vocabulary);
  return activity;
}

/* ------------------------------------------------------------------------------------- *
 * Words
 * ------------------------------------------------------------------------------------- */

/**
 * "2 hours ago", or "just now"; the absolute instant goes in a `title`. The one past-only rule,
 * `relativeAgo` (`lib/time/displayTime.ts`, #1017 review): a future instant reads "just now".
 */
export function relativeWhen(iso: string | null, now: Date = new Date()): string {
  return relativeAgo(iso, now);
}

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * When an import ended (#1216). Within a day it is the elapsed time, "just now", "20 minutes ago"
 * or "4 hours ago", which no calendar can contradict. From a day on it is the calendar of the
 * reader's display zone: "yesterday", or "on Oct 13" ("on Dec 30, 2025" in another year).
 * Elapsed hours are never rounded into a day word: a run 31 hours back on the day before
 * yesterday reads its date, never "yesterday". Empty for a missing or unreadable instant, as
 * `relativeAgo`. Any past instant a list shows as a day word reads through it (the connections
 * list's Last import and Last successful refresh, a stuck import's start, the Transformations
 * status line and test results), so no two screens disagree about the same day.
 */
export function endedWhen(iso: string | null, zone: string, now: Date = new Date()): string {
  const ago = relativeAgo(iso, now);
  if (!ago || !iso) return ago;
  const then = new Date(iso);
  if (now.getTime() - then.getTime() < DAY_MS) return ago;
  const thenDay = calendarDayInZone(then, zone);
  const today = calendarDayInZone(now, zone);
  if (!thenDay || !today) return ago;
  const days = Math.round(
    (Date.UTC(today.year, today.month - 1, today.day) -
      Date.UTC(thenDay.year, thenDay.month - 1, thenDay.day)) /
      DAY_MS,
  );
  // A 25-hour day (clocks fall back) can put more than 24 hours between two instants of the
  // same calendar day: that is still the elapsed hours ("24 hours ago"), never "yesterday", which
  // `relativeAgo` would round 24.5 hours into.
  if (days <= 0) {
    const hours = Math.floor((now.getTime() - then.getTime()) / (60 * 60 * 1000));
    return new Intl.RelativeTimeFormat("en", { numeric: "auto" }).format(-hours, "hour");
  }
  if (days === 1) return "yesterday";
  const date = new Intl.DateTimeFormat("en-US", {
    timeZone: canRenderZone(zone) ? zone : FALLBACK_ZONE,
    month: "short",
    day: "numeric",
    ...(thenDay.year === today.year ? {} : { year: "numeric" }),
  }).format(then);
  return `on ${date}`;
}

/** The ended word in `zone` when one is given, else the elapsed `relativeAgo`. */
function whenEnded(iso: string | null, now: Date, zone: string | undefined): string {
  return zone ? endedWhen(iso, zone, now) : relativeWhen(iso, now);
}

export type ImportDensity = "row" | "panel" | "page";

/**
 * The editor header's import detail beside its status badge (which already says Running): "Queued",
 * or the step, "Step 2 of 3". Null when nothing is moving or the step is not known yet.
 */
export function importStepText(activity: RunActivity, now: Date = new Date()): string | null {
  if (activity.phase === "queued") return "Queued";
  if (activity.phase !== "running") return null;
  if (activity.stepIndex !== null && activity.stepTotal !== null) {
    const head = stepOfLine(activity.stepIndex, activity.stepTotal);
    const sending = sendingFilesPhrase(activity, now);
    return sending ? `${head} · ${sending}` : head;
  }
  return null;
}

/**
 * #1672: the time so far in whole minutes ("under a minute", "2m", "1h 05m"). The surfaces that
 * show it tick every `NOW_TICK_MS` (30 s), so a seconds figure would sit still and then jump.
 */
function minutesSoFar(seconds: number): string {
  const minutes = Math.floor(Math.max(0, seconds) / 60);
  if (minutes < 1) return "under a minute";
  if (minutes < 60) return `${minutes}m`;
  return `${Math.floor(minutes / 60)}h ${String(minutes % 60).padStart(2, "0")}m`;
}

/**
 * #1672: "Sending files to Snowflake · 2m so far" while the load's push sends the tables' files,
 * else null. The time so far is from the push's start to `now`, in whole minutes.
 */
export function sendingFilesPhrase(activity: RunActivity, now: Date = new Date()): string | null {
  if (activity.phase !== "running" || activity.sendingFilesSince === null) return null;
  const started = Date.parse(activity.sendingFilesSince);
  if (!Number.isFinite(started)) return SENDING_FILES_SENTENCE;
  const seconds = (now.getTime() - started) / 1000;
  return `${SENDING_FILES_SENTENCE} · ${minutesSoFar(seconds)} so far`;
}

/**
 * The step with its word, "Queued" or "Running · Step 2 of 3": the header's import badge before
 * the header carried the connection status (2026-09-28). The header now shows `importStepText`
 * beside the status badge; this stays as the one-string form its tests pin.
 */
export function importBadgeText(activity: RunActivity): string | null {
  if (activity.phase === "queued") return "Queued";
  if (activity.phase !== "running") return null;
  if (activity.stepIndex !== null && activity.stepTotal !== null) {
    return `Running · ${stepOfLine(activity.stepIndex, activity.stepTotal)}`;
  }
  return "Running";
}

function finishedLine(
  activity: RunActivity,
  density: ImportDensity,
  now: Date,
  zone: string | undefined,
): string {
  const when = whenEnded(activity.finishedAt, now, zone);
  const head = when ? `Finished ${when}` : "Finished";
  if (density === "row") return head;
  const parts = [head];
  const into = rowsIntoTables(activity);
  if (into) parts.push(into);
  if (activity.durationSeconds !== null) parts.push(formatDuration(activity.durationSeconds));
  return parts.join(" · ");
}

/**
 * "21,042 rows into 12 tables, 8 had no rows": a finished import's rows and the tables that took
 * them, with the tables the source sent no row for counted apart (#2392); null when either count
 * is unknown.
 */
export function rowsIntoTables(activity: RunActivity): string | null {
  if (activity.rowsLoaded === null || activity.tables === null) return null;
  const empty = Math.min(activity.tablesNoRows ?? 0, activity.tables);
  // #2392 (review): every table empty at the source reads that, never "0 rows into 0 tables".
  if (empty > 0 && empty === activity.tables) return allTablesEmptySentence(empty);
  const into = activity.tables - empty;
  const text = `${plural(activity.rowsLoaded, "row")} into ${formatCount(into)} ${
    into === 1 ? "table" : "tables"
  }`;
  return empty > 0 ? `${text}, ${formatCount(empty)} had no rows` : text;
}

/**
 * #2389: how long an extract may show no activity before the page says so plainly. A connector
 * that reports rows read sends a tick at most once per table every 60 seconds, and only when the
 * count moved (owner decision on #2389, 10/27), so a table stuck on one slow slice sends nothing,
 * and one that reports only completions can be quiet for minutes on one large table: ten minutes
 * is long past a tick interval and short enough that nobody waits half an hour wondering. It is
 * only ever stated as a fact, never as stalled or failed: the import is still running.
 */
export const QUIET_AFTER_MINUTES = 10;

/** #2389: the quiet time in words: "12 minutes", "1 hour", "1 hour and 5 minutes". */
function quietFor(ms: number): string {
  const minutes = Math.floor(Math.max(0, ms) / 60_000);
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  const minuteWords = `${rest} ${rest === 1 ? "minute" : "minutes"}`;
  if (hours === 0) return minuteWords;
  const hourWords = `${hours} ${hours === 1 ? "hour" : "hours"}`;
  return rest === 0 ? hourWords : `${hourWords} and ${minuteWords}`;
}

/** #2389: the milliseconds since the extract last showed activity, or null when not known. */
function quietMs(activity: RunActivity, now: Date): number | null {
  if (activity.phase !== "running" || activity.step !== "extract") return null;
  if (activity.lastActivityAt === null) return null;
  const last = Date.parse(activity.lastActivityAt);
  if (!Number.isFinite(last)) return null;
  return Math.max(0, now.getTime() - last);
}

/**
 * #2389: the run page's and the connection card's line under the step while the extract runs,
 * from skyData's own records, so the page is never dark. Before the connector has reported
 * anything (the job may still be starting): "Starting the import from Mouseflow. Started 40
 * seconds ago." Once it has: "Still reading from Mouseflow. Last activity 40 seconds ago." After
 * `QUIET_AFTER_MINUTES` with nothing new, in either state: "No progress reported for 12 minutes
 * while importing from Mouseflow. The import is still running." skyData's connector is what
 * reports progress, so the line never says the source is the one not reporting. Null in every
 * other step.
 */
export function livenessLine(activity: RunActivity, now: Date = new Date()): string | null {
  const quiet = quietMs(activity, now);
  if (quiet === null) return null;
  const named = activity.sourceName?.trim();
  const source = named ? named : "the source";
  if (quiet >= QUIET_AFTER_MINUTES * 60_000) {
    return `No progress reported for ${quietFor(quiet)} while importing from ${source}. The import is still running.`;
  }
  if (!activity.connectorReported) {
    return `Starting the import from ${source}. Started ${activityAgo(quiet)}.`;
  }
  return `Still reading from ${source}. Last activity ${activityAgo(quiet)}.`;
}

/**
 * #2389: how long ago the extract last showed activity, under `QUIET_AFTER_MINUTES`: "just now"
 * under 10 seconds, then whole seconds, then whole minutes. The strip ticks every
 * `LIVENESS_TICK_MS` while it shows this, so the seconds move.
 */
function activityAgo(ms: number): string {
  const seconds = Math.floor(ms / 1000);
  if (seconds < 10) return "just now";
  if (seconds < 60) return `${seconds} seconds ago`;
  const minutes = Math.floor(seconds / 60);
  return `${minutes} ${minutes === 1 ? "minute" : "minutes"} ago`;
}

/** #2389: how often a strip that shows the liveness line re-reads the clock. */
export const LIVENESS_TICK_MS = 10_000;

/** #2389: whether the strip shows the liveness line, so it ticks faster. */
export function showsLiveness(activity: RunActivity): boolean {
  return (
    activity.phase === "running" && activity.step === "extract" && activity.lastActivityAt !== null
  );
}

/**
 * #2389: the list row's words once the extract has been quiet for `QUIET_AFTER_MINUTES`: "No
 * progress reported for 12 minutes". Null before that, and in every other step.
 */
export function quietPhrase(activity: RunActivity, now: Date = new Date()): string | null {
  const quiet = quietMs(activity, now);
  if (quiet === null || quiet < QUIET_AFTER_MINUTES * 60_000) return null;
  return `No progress reported for ${quietFor(quiet)}`;
}

/** The running line: "Step 2 of 3: Loading into Snowflake", or the row's terse form. */
function runningLine(activity: RunActivity, density: ImportDensity, now: Date): string {
  if (activity.stepIndex === null || activity.stepTotal === null) return "Running";
  const head = stepOfLine(activity.stepIndex, activity.stepTotal);
  const retry = activity.attempt !== null && activity.attempt > 1;
  if (density === "row") return runningRowLine(activity, head, retry, now);
  const sentence = sendingFilesPhrase(activity, now) ?? activity.sentence ?? activity.stepLabel;
  const line = sentence ? `${head}: ${sentence}` : head;
  return retry ? `${line} · attempt ${activity.attempt}` : line;
}

/** The row's running line: "Step 2 of 3 · Loading · 7 of 40 tables · attempt 2". */
function runningRowLine(activity: RunActivity, head: string, retry: boolean, now: Date): string {
  const parts = [head];
  // #1672: while the push sends the files, no table can have finished: the row says so.
  const sending = sendingFilesPhrase(activity, now);
  if (sending) parts.push(sending);
  else if (activity.step) parts.push(stepVerb(activity.step));
  const tables = tablesPhrase({ done: activity.tablesDone, total: activity.tablesTotal });
  if (!sending && tables && (activity.step === "extract" || activity.step === "load")) {
    parts.push(tables);
  }
  // #2389: a quiet extract says so on the row too, never that it stopped.
  const quiet = quietPhrase(activity, now);
  if (quiet) parts.push(quiet);
  if (retry) parts.push(`attempt ${activity.attempt}`);
  return parts.join(" · ");
}

/**
 * #817: the status word, then the tables that did not load ("Partly failed: 2 of 9 tables did
 * not load"); the word alone when every table loaded (the transformations failed).
 */
function partialLine(activity: RunActivity, density: ImportDensity): string {
  const { tables, tablesLoaded } = activity;
  return tables !== null && tablesLoaded !== null && tables > tablesLoaded && density !== "row"
    ? partlyFailedSentence({
        connections: 0,
        failedConnectionNames: [],
        tables,
        tablesNotLoaded: Math.max(0, tables - tablesLoaded),
      })
    : PARTLY_FAILED_LABEL;
}

function failedLine(
  activity: RunActivity,
  density: ImportDensity,
  now: Date,
  zone: string | undefined,
): string {
  if (activity.failedStepIndex !== null && activity.stepTotal !== null) {
    // #1391 item 28: "Failed", not "Stopped": the last step (Finish the run) still runs and
    // succeeds after an earlier step fails, so the run did not stop there.
    const head = `Failed at step ${activity.failedStepIndex} of ${activity.stepTotal}`;
    return activity.failedStepLabel && density !== "row"
      ? `${head}: ${activity.failedStepLabel}.`
      : head;
  }
  const when = whenEnded(activity.finishedAt, now, zone);
  return when ? `Failed ${when}` : "Import failed";
}

/**
 * The one line under the badge (row) or above the stepper (panel, page). Status words ride
 * with the badge colour, so the line alone must say what happened.
 */
export function activityLine(
  activity: RunActivity,
  density: ImportDensity,
  now: Date = new Date(),
  /** The reader's display zone: an ended import's day word follows its calendar (#1216). */
  zone?: string,
): string {
  switch (activity.phase) {
    case "loading":
      return "Loading the last import…";
    case "unavailable":
      return "Last import no longer available";
    case "never":
      return "Not imported yet";
    case "queued": {
      const ahead = activity.queuedReason;
      return ahead ? `Queued · ${ahead}` : "Queued · Waiting to start";
    }
    case "running":
      return runningLine(activity, density, now);
    case "succeeded":
      return finishedLine(activity, density, now, zone);
    case "partial":
      return partialLine(activity, density);
    case "failed":
      return failedLine(activity, density, now, zone);
    case "cancelled": {
      const when = whenEnded(activity.finishedAt, now, zone);
      return when ? `Cancelled ${when}` : "Cancelled";
    }
  }
}

/**
 * What the live line announces: it changes only on a step or phase change, never per table,
 * so a screen reader hears one sentence per step (ux-s10.md §2).
 */
export function activityAnnouncement(activity: RunActivity, connection: string): string {
  switch (activity.phase) {
    case "queued":
      return `${connection} import queued.`;
    case "running":
      if (activity.stepIndex !== null && activity.stepTotal !== null) {
        // #1672: the push phase is a phase change too, so it is heard once.
        const words =
          activity.sendingFilesSince !== null ? SENDING_FILES_SENTENCE : activity.stepLabel;
        const label = words ? `: ${words}` : "";
        return `${connection} import: step ${activity.stepIndex} of ${activity.stepTotal}${label}.`;
      }
      return `${connection} import running.`;
    case "succeeded":
      return `${connection} import finished.`;
    case "partial":
      return `${connection} import partly failed.`;
    case "failed":
      return activity.failedStepIndex !== null && activity.stepTotal !== null
        ? `${connection} import failed at step ${activity.failedStepIndex} of ${activity.stepTotal}.`
        : `${connection} import failed.`;
    case "cancelled":
      return `${connection} import cancelled.`;
    default:
      return "";
  }
}

/** The progress bar's value (0-100), or undefined for the indeterminate queued bar. */
export function activityProgress(activity: RunActivity): number | undefined {
  if (activity.phase === "queued" || activity.phase === "loading") return undefined;
  if (activity.phase !== "running") return 100;
  if (activity.stepIndex === null || activity.stepTotal === null || activity.stepTotal === 0) {
    return undefined;
  }
  const doneBefore = activity.stepIndex - 1;
  let fraction = 0;
  if (activity.tablesDone !== null && activity.tablesTotal !== null && activity.tablesTotal > 0) {
    fraction = Math.min(1, activity.tablesDone / activity.tablesTotal);
  }
  return Math.round(((doneBefore + fraction) / activity.stepTotal) * 100);
}

/** True while the import is expected to keep changing. */
export function isActivityLive(activity: RunActivity): boolean {
  return activity.phase === "queued" || activity.phase === "running";
}
